import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  galleryOrder,
  animalGalleryOrder,
  resortGalleryImages,
  animalGalleryImages,
} from '../config/info';

const AUTO_MS = 5500;

function numericPrefix(p) {
  const base = p.split('/').pop() || p;
  const m = base.match(/^(\d{1,4})[-_\s]/);
  return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
}

function orderRank(p, orderList) {
  if (!Array.isArray(orderList) || orderList.length === 0) return Number.POSITIVE_INFINITY;
  const lower = p.toLowerCase();
  const idx = orderList.findIndex((frag) => frag && lower.includes(String(frag).toLowerCase()));
  return idx >= 0 ? idx : Number.POSITIVE_INFINITY;
}

function sortKeys(keys, orderList) {
  return keys.slice().sort((a, b) => {
    const ra = orderRank(a, orderList);
    const rb = orderRank(b, orderList);
    if (ra !== rb) return ra - rb;
    const na = numericPrefix(a);
    const nb = numericPrefix(b);
    if (na !== nb) return na - nb;
    return a.localeCompare(b);
  });
}

/** Group `./a.jpg` vs `./rooms/b.jpg` → sections with folder key '' or 'rooms' */
function groupByTopFolder(keys) {
  const map = new Map();
  for (const k of keys) {
    const slash = k.indexOf('/');
    const folder = slash === -1 ? '' : k.slice(0, slash);
    if (!map.has(folder)) map.set(folder, []);
    map.get(folder).push(k);
  }
  return map;
}

function sectionHeading(folderKey) {
  if (!folderKey) return 'Property';
  return folderKey.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function GallerySubCarousel({ sectionId, overline, title, subtitle, items, imageAspectRatio = '4 / 3' }) {
  const [idx, setIdx] = useState(0);
  const len = items.length;

  const go = useCallback(
    (delta) => {
      if (len <= 0) return;
      setIdx((i) => (i + delta + len) % len);
    },
    [len]
  );

  useEffect(() => {
    setIdx(0);
  }, [items]);

  useEffect(() => {
    if (len <= 1) return;
    const t = setInterval(() => go(1), AUTO_MS);
    return () => clearInterval(t);
  }, [len, go]);

  if (len === 0) return null;

  return (
    <Box
      component="section"
      aria-labelledby={sectionId ? `${sectionId}-heading` : undefined}
      sx={{ mb: 5, '&:last-of-type': { mb: 0 } }}
    >
      {overline ? (
        <Typography
          variant="overline"
          sx={{ letterSpacing: 3, color: 'var(--color-warm-gray)', fontWeight: 500, fontSize: '0.85rem' }}
        >
          {overline}
        </Typography>
      ) : null}
      <Typography
        id={sectionId ? `${sectionId}-heading` : undefined}
        component="h3"
        sx={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: { xs: '1.65rem', md: '2rem' },
          fontWeight: 600,
          mt: overline ? 0.5 : 0,
          mb: subtitle ? 0.75 : 2,
          color: 'var(--color-charcoal)',
        }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: 'var(--color-warm-gray)', fontSize: '0.95rem', mb: 2, maxWidth: 720 }}>
          {subtitle}
        </Typography>
      ) : null}

      <Box
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid var(--color-sand)',
          bgcolor: 'var(--color-charcoal)',
          aspectRatio: imageAspectRatio,
        }}
      >
        {items.map((item, i) => (
          <Box
            key={`${item.url}-${i}`}
            component="img"
            src={item.url}
            alt={item.name ? `${item.name} — ${item.alt}` : item.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: i === idx ? 1 : 0,
              transition: 'opacity 0.7s ease-in-out',
              pointerEvents: 'none',
            }}
          />
        ))}

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(44,41,38,0.15) 0%, transparent 35%, rgba(44,41,38,0.55) 100%)',
            pointerEvents: 'none',
          }}
        />

        {len > 1 && (
          <>
            <IconButton
              onClick={() => go(-1)}
              aria-label="Previous image"
              sx={{
                position: 'absolute',
                left: { xs: 4, sm: 12 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.35)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
              }}
            >
              <ChevronLeft sx={{ fontSize: 36 }} />
            </IconButton>
            <IconButton
              onClick={() => go(1)}
              aria-label="Next image"
              sx={{
                position: 'absolute',
                right: { xs: 4, sm: 12 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.35)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
              }}
            >
              <ChevronRight sx={{ fontSize: 36 }} />
            </IconButton>
          </>
        )}

        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 0.75,
            zIndex: 2,
            flexWrap: 'wrap',
            px: 2,
          }}
        >
          {items.map((_, i) => (
            <Box
              key={i}
              component="button"
              type="button"
                aria-label={items[i]?.name ? `Show ${items[i].name}` : `Go to image ${i + 1}`}
              onClick={() => setIdx(i)}
              sx={{
                width: i === idx ? 22 : 8,
                height: 8,
                borderRadius: 4,
                border: 'none',
                p: 0,
                cursor: 'pointer',
                bgcolor: i === idx ? 'var(--color-cream)' : 'rgba(250,248,245,0.45)',
                transition: 'width 0.25s ease, background-color 0.2s',
                '&:hover': { bgcolor: i === idx ? 'var(--color-cream)' : 'rgba(250,248,245,0.7)' },
              }}
            />
          ))}
        </Box>

        {(items[idx]?.name || len > 1) && (
          <Typography
            component="p"
            sx={{
              position: 'absolute',
              bottom: 44,
              left: 16,
              right: 16,
              color: 'rgba(250,248,245,0.95)',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              lineHeight: 1.35,
              textShadow: '0 1px 10px rgba(0,0,0,0.75)',
              zIndex: 2,
              textAlign: 'center',
            }}
          >
            {items[idx]?.name ? (
              <>
                {items[idx].name}
                {len > 1 ? (
                  <Box component="span" sx={{ fontWeight: 500, opacity: 0.88, fontSize: '0.82em', ml: 0.75 }}>
                    ({idx + 1} / {len})
                  </Box>
                ) : null}
              </>
            ) : (
              len > 1 && `${idx + 1} / ${len}`
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function sectionsFromManifest(entries, orderList, altPrefix, rootSectionTitle) {
  if (!entries || entries.length === 0) return [];
  const entryByRel = new Map(entries.map((e) => [e.rel, e]));
  const keys = entries.map((e) => e.rel);
  const grouped = groupByTopFolder(keys);

  const folderKeys = [...grouped.keys()].sort((a, b) => {
    if (a === '') return -1;
    if (b === '') return 1;
    return a.localeCompare(b);
  });

  return folderKeys.map((folder) => {
    const folderKeysSorted = sortKeys(grouped.get(folder) || [], orderList);
    const orderedEntries = folderKeysSorted
      .map((k) => entryByRel.get(k))
      .filter((e) => e && e.url);
    const items = orderedEntries.map((entry, i) => {
      const trimmed = typeof entry.name === 'string' ? entry.name.trim() : '';
      return {
        rel: entry.rel,
        url: entry.url,
        ...(trimmed ? { name: trimmed } : {}),
        alt: `${altPrefix} ${i + 1}`,
      };
    });
    return {
      key: folder || 'root',
      title: folder ? sectionHeading(folder) : rootSectionTitle,
      items,
    };
  });
}

const GallerySection = () => {
  const gallerySections = useMemo(
    () =>
      sectionsFromManifest(
        resortGalleryImages,
        Array.isArray(galleryOrder) ? galleryOrder : [],
        'Resort photo',
        'Property'
      ),
    []
  );

  const animalSections = useMemo(
    () =>
      sectionsFromManifest(
        animalGalleryImages,
        Array.isArray(animalGalleryOrder) ? animalGalleryOrder : [],
        'Wildlife photo',
        'Animals nearby'
      ),
    []
  );

  const hasGallery = gallerySections.some((s) => s.items.length > 0);
  const hasAnimals = animalSections.some((s) => s.items.length > 0);

  if (!hasGallery && !hasAnimals) return null;

  return (
    <Box
      id="gallery"
      sx={{
        py: { xs: 6, md: 8 },
        px: 3,
        bgcolor: 'var(--color-cream)',
        borderTop: '1px solid var(--color-sand)',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {hasGallery ? (
          <>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 3, color: 'var(--color-warm-gray)', fontWeight: 500, fontSize: '0.85rem' }}
            >
              Photos
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 500,
                mt: 1,
                mb: 3,
                color: 'var(--color-charcoal)',
              }}
            >
              Gallery
            </Typography>
            <Typography sx={{ color: 'var(--color-warm-gray)', fontSize: '0.95rem', mb: 4, maxWidth: 720 }}>
              Each subfolder under the gallery is its own carousel, stacked below. Root-level photos are grouped
              under <strong>Property</strong>.
            </Typography>

            {gallerySections.map((s) => (
              <GallerySubCarousel
                key={`gallery-${s.key}`}
                sectionId={`gallery-${s.key}`}
                title={s.title}
                items={s.items}
              />
            ))}
          </>
        ) : null}

        {hasAnimals ? (
          <Box sx={{ mt: hasGallery ? 6 : 0 }}>
            <Typography
              variant="overline"
              sx={{ letterSpacing: 3, color: 'var(--color-warm-gray)', fontWeight: 500, fontSize: '0.85rem' }}
            >
              Nature
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 500,
                mt: 1,
                mb: 1,
                color: 'var(--color-charcoal)',
              }}
            >
              Wildlife nearby
            </Typography>
            <Typography sx={{ color: 'var(--color-warm-gray)', fontSize: '0.95rem', mb: 4, maxWidth: 720 }}>
              Wildlife you may spot around Valparai &amp; Sholayar. Optional subfolders become extra carousels below.
            </Typography>

            {animalSections.map((s) => (
              <GallerySubCarousel
                key={`animals-${s.key}`}
                sectionId={`animals-${s.key}`}
                overline={s.key !== 'root' ? 'Animals nearby' : undefined}
                title={s.title}
                items={s.items}
                imageAspectRatio="1 / 1"
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default GallerySection;
